import { Form, FormHandlers } from '@doveit/bricks';
import React from 'react';
import { AgentStatus, Department, JobApplicationType } from '../../../domain/types';
import { jobApplicationTypeLabels, userRoleLabels } from '../../../labels';
import JobPositionsSelect from '../../../job-position/containers/job-positions-select/JobPositionsSelect';
import { provinceSelectOptions } from '../../../select-options';
import { jobApplicationHrUsers } from '../../constants';
import { BooleanString } from '../../../utils/boolean-string/booleanString';
import { asSelectOptions } from '../../../select-options/utils/asSelectOptions';
import UserAutoComplete from '../../../auth/components/user-autocomplete/UserAutoComplete';
import AgentAutoComplete from '../../../containers/agent-autocomplete/AgentAutoComplete';
import { CustomUser, UserRole } from '../../../types';
import { Agent, JobPosition } from '../../../providers/api/dtos';
import getShortAgentName from '../../../utils/agent/getShortAgentName';
import { getAgentName } from '../../../agent/utils/utils';
import { jobApplicationSerpFiltersFormSchema } from './JobApplicationSerpFiltersForm.schema';

export interface JobApplicationSerpFiltersFormModel {
  type: JobApplicationType | '',
  jobPositionIds: string[],
  provinces: string[],
  supervisorEmails: string[],
  assigneeInterviewers: (Agent | CustomUser)[],
  createdAtFrom: string,
  createdAtTo: string,
  source: string,
  isFranchising: BooleanString,
  hasLicense: BooleanString,
}

export interface JobApplicationSerpFiltersFormProps extends React.AriaAttributes {
  initialValues?: Partial<JobApplicationSerpFiltersFormModel>,
  hiddenFields?: Partial<Record<keyof JobApplicationSerpFiltersFormModel, boolean>>,
  loading?: boolean,
  disabled?: boolean,
  innerRef?: React.MutableRefObject<FormHandlers>,
  allowedAssigneeType?: 'agents' | 'all',
  agentsCoordinator?: string,
  onSubmit: (values: JobApplicationSerpFiltersFormModel) => void,
}

export const JOB_APPLICATION_SERP_FILTERS_FORM_DEFAULT_VALUES: JobApplicationSerpFiltersFormModel = {
  type: '',
  jobPositionIds: [],
  provinces: [],
  supervisorEmails: [],
  assigneeInterviewers: [],
  createdAtFrom: '',
  createdAtTo: '',
  source: '',
  isFranchising: '',
  hasLicense: '',
};

const JobApplicationSerpFiltersForm: React.FC<JobApplicationSerpFiltersFormProps> = ({
  initialValues = {},
  hiddenFields = {},
  loading = false,
  disabled = false,
  allowedAssigneeType = 'all',
  agentsCoordinator,
  innerRef,
  onSubmit,
  ...rest
}) => {
  const mergedInitialValues = React.useMemo(() => ({
    ...JOB_APPLICATION_SERP_FILTERS_FORM_DEFAULT_VALUES,
    ...initialValues,
  }), [initialValues]);
  const getShortName = (user: any) => {
    if (user.shortName) return user.shortName;

    if (user.given_name && user.family_name) {
      return `${user.given_name.charAt(0).toUpperCase()}. ${user.family_name}`;
    }

    return user.name;
  };

  const userPrintSuggestion = React.useCallback((user: any | any[]) => {
    if (Array.isArray(user)) return '';
    return getShortName(user);
  }, []);

  const userPrintLabelSuggestion = React.useCallback((user: CustomUser | CustomUser[]) => {
    if (Array.isArray(user)) return '';

    const rolesLabel = user['https://backoffice.dove.it/roles']
      ?.map((role: UserRole) => userRoleLabels[role])
      .join(', ');

    return rolesLabel ? `${user.name} - ${rolesLabel}` : user.name;
  }, []);

  const agentPrintSuggestion = React.useCallback((agent: any | any[]) => {
    if (Array.isArray(agent)) return '';
    if (typeof agent.name === 'string') return agent.name; // workaround perchè il parametro gestisce sia CustomUser che Agent
    return getShortAgentName(agent.name);
  }, []);

  const agentPrintLabelSuggestion = React.useCallback((agent: Agent | Agent[]) => {
    if (Array.isArray(agent)) return '';

    return getAgentName(agent)!;
  }, []);

  return (
    <Form
      initialValues={mergedInitialValues}
      validationSchema={jobApplicationSerpFiltersFormSchema}
      loading={loading}
      disabled={disabled}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ values, setFieldValue }) => {
        const handleTypeChange = () => {
          setFieldValue('jobPositionIds', []);
          setFieldValue('assigneeInterviewers', []);
          setFieldValue('provinces', []);
          setFieldValue('hasLicense', '');
          setFieldValue('isFranchising', '');
        };

        const filterJobPositionsByType = (jobPosition: JobPosition) => {
          if (!values.type) return true;

          const isSalesType = values.type === JobApplicationType.SALES;
          const isSalesDepartment = jobPosition.department === Department.SALES;

          return isSalesType === isSalesDepartment;
        };

        const showAgentAutoCompleteForAssignees = allowedAssigneeType === 'agents' || values.type === JobApplicationType.SALES;

        return (
          <>
            {!hiddenFields.type && (
              <Form.Item>
                <Form.Select
                  label="Tipologia"
                  name="type"
                  aria-label="Campo per specificare la tipologia"
                  onChange={handleTypeChange}
                  options={[{
                    value: '',
                    label: 'Seleziona...',
                  }, ...Object.values(JobApplicationType).map((value) => ({
                    value,
                    label: jobApplicationTypeLabels[value],
                  })),
                  ]}
                />
              </Form.Item>
            )}

            {!hiddenFields.jobPositionIds && (
              <Form.Item>
                <JobPositionsSelect
                  label="Posizione"
                  name="jobPositionIds"
                  aria-label="Campo per specificare la posizione"
                  filterJobPositions={filterJobPositionsByType}
                  allowMultipleValues
                />
              </Form.Item>
            )}

            {!hiddenFields.provinces && (
              <Form.Item>
                <Form.MultiSelect
                  name="provinces"
                  label="Provincia"
                  options={provinceSelectOptions}
                  disabled={values.type === JobApplicationType.GENERIC}
                  aria-label="Campo per specificare la provincia"
                />
              </Form.Item>
            )}

            {!hiddenFields.supervisorEmails && (
              <Form.Item>
                <Form.MultiSelect
                  name="supervisorEmails"
                  label="Gestore HR"
                  options={jobApplicationHrUsers.map((user) => ({
                    label: user.name,
                    value: user.email,
                  }))}
                  aria-label="Campo per specificare il gestore"
                />
              </Form.Item>
            )}

            {!hiddenFields.assigneeInterviewers && (
              <Form.Item>
                {!showAgentAutoCompleteForAssignees && (
                  <UserAutoComplete
                    name="assigneeInterviewers"
                    label="Assegnatario / Esaminatore"
                    aria-label="Campo per specificare l'assegnatario o esaminatore"
                    printSuggestion={userPrintSuggestion}
                    printLabelSuggestion={userPrintLabelSuggestion}
                    roles={values.type === JobApplicationType.GENERIC ? [UserRole.ADMIN, UserRole.HR] : Object.values(UserRole)}
                    isMulti
                  />
                )}

                {showAgentAutoCompleteForAssignees && (
                  <AgentAutoComplete
                    name="assigneeInterviewers"
                    label="Assegnatario / Esaminatore"
                    aria-label="Campo per specificare l'assegnatario o esaminatore"
                    status={AgentStatus.ACTIVE}
                    startFrom={agentsCoordinator}
                    printSuggestion={agentPrintSuggestion}
                    printLabelSuggestion={agentPrintLabelSuggestion}
                    isMulti
                  />
                )}
              </Form.Item>
            )}

            {(!hiddenFields.createdAtFrom || !hiddenFields.createdAtTo) && (
              <Form.Group label="Data di creazione">
                {!hiddenFields.createdAtFrom && (
                  <Form.Item size={{ MD: 1 / 2 }}>
                    <Form.Input
                      name="createdAtFrom"
                      type="date"
                      aria-label="Campo per inserire la data di creazione (da)"
                      prefixElement="Dal"
                    />
                  </Form.Item>
                )}

                {!hiddenFields.createdAtTo && (
                  <Form.Item size={{ MD: 1 / 2 }}>
                    <Form.Input
                      name="createdAtTo"
                      type="date"
                      aria-label="Campo per inserire la data di creazione (a)"
                      prefixElement="Al"
                      min={values.createdAtFrom}
                    />
                  </Form.Item>
                )}
              </Form.Group>
            )}

            {!hiddenFields.source && (
              <Form.Item>
                <Form.Input
                  name="source"
                  label="Sorgente"
                  aria-label="Campo per specificare la sorgente"
                />
              </Form.Item>
            )}

            {!hiddenFields.isFranchising && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Select
                  name="isFranchising"
                  label="Franchising?"
                  disabled={values.type === JobApplicationType.GENERIC}
                  aria-label="Campo per specificare il franchising"
                  options={asSelectOptions({
                    true: 'Franchising',
                    false: 'Non franchising',
                  })}
                />
              </Form.Item>
            )}

            {!hiddenFields.hasLicense && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Select
                  label="Con patentino?"
                  name="hasLicense"
                  aria-label="Campo per specificare il patentino"
                  disabled={values.type === JobApplicationType.GENERIC}
                  options={asSelectOptions({
                    true: 'Con patentino',
                    false: 'Senza patentino',
                  })}
                />
              </Form.Item>
            )}
          </>
        );
      }}
    </Form>
  );
};

export default JobApplicationSerpFiltersForm;
